import { useState, useCallback } from "react";
import { Plus, Pencil, Trash, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryManager({ categories, onUpdate }) {
  const [newCategory, setNewCategory] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const handleAddCategory = useCallback(async () => {
    if (!newCategory.trim()) {
      toast.error("El nombre de la categoría es requerido");
      return;
    }

    try {
      await axios.post(`${API}/categories`, { nombre: newCategory.trim() });
      toast.success("Categoría creada");
      setNewCategory("");
      onUpdate();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.detail || "Error al crear la categoría");
    }
  }, [newCategory, onUpdate]);

  const handleUpdateCategory = useCallback(async (id) => {
    if (!editingName.trim()) {
      toast.error("El nombre de la categoría es requerido");
      return;
    }

    try {
      await axios.put(`${API}/categories/${id}`, { nombre: editingName.trim() });
      toast.success("Categoría actualizada");
      setEditingId(null);
      setEditingName("");
      onUpdate();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Error al actualizar la categoría");
    }
  }, [editingName, onUpdate]);

  const handleDeleteCategory = useCallback(async (id, nombre) => {
    if (nombre === "Otros") {
      toast.error("No puedes eliminar la categoría 'Otros'");
      return;
    }

    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success("Categoría eliminada");
      onUpdate();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Error al eliminar la categoría");
    }
  }, [onUpdate]);

  const startEditing = (cat) => {
    setEditingId(cat.id);
    setEditingName(cat.nombre);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  return (
    <div className="space-y-4">
      {/* Add new category */}
      <div className="flex gap-2">
        <Input
          data-testid="new-category-input"
          placeholder="Nueva categoría..."
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
        />
        <Button
          data-testid="add-category-btn"
          onClick={handleAddCategory}
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          <Plus size={16} />
        </Button>
      </div>

      {/* Categories list */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200"
          >
            {editingId === cat.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  data-testid={`edit-category-input-${cat.id}`}
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleUpdateCategory(cat.id)}
                  className="flex-1"
                  autoFocus
                />
                <Button
                  data-testid={`save-category-${cat.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleUpdateCategory(cat.id)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <Check size={16} />
                </Button>
                <Button
                  data-testid={`cancel-edit-${cat.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={cancelEditing}
                  className="text-slate-600 hover:text-slate-700"
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <>
                <span className="font-medium text-slate-900">{cat.nombre}</span>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid={`edit-category-${cat.id}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => startEditing(cat)}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    <Pencil size={16} />
                  </Button>
                  {cat.nombre !== "Otros" && (
                    <Button
                      data-testid={`delete-category-${cat.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteCategory(cat.id, cat.nombre)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        Al cambiar el nombre de una categoría, todos los artículos asignados se actualizan automáticamente.
      </p>
    </div>
  );
}
